import { useEffect, useState } from 'react';
import { loadTakiBuildStamp } from '@taki/runtime-config';
import type { TakiBuildStamp } from '@taki/runtime-config';

/**
 * Lecture du tampon de build (`/taki-build.json`), cote React.
 *
 * PERMANENT, et c'est important : ce branchement a d'abord ete ecrit pour la
 * ligne de version du menu DEV, mais la bande "app deprecated" (`DeprecatedBand`)
 * le consomme aussi, et elle, elle existe en PRODUCTION. Le fichier vit donc a
 * la racine du shell et pas dans `dev/` : retirer la greffe DEV ne doit pas
 * emporter la bande deprecated avec elle.
 *
 * Le chargeur lui-meme (le fetch, les erreurs typees, la verification du type de
 * contenu) vit dans `@taki/runtime-config` : voir `load-build-stamp.ts`. Ici, on
 * ne fait que l'etat React, le cache et les quatre cas.
 *
 * POURQUOI UN CACHE DE MODULE : la ligne de version est montee dans le layout
 * racine du routeur, donc elle traverse chaque navigation. Sans cache, chaque
 * changement d'app relancerait un fetch pour une information qui ne change pas
 * pendant la vie de l'onglet. Un rechargement de page repart de zero, ce qui
 * suffit : apres un `git checkout`, on recharge.
 *
 * AUCUN APPEL A /__taki/dev/git ICI, JAMAIS. Cette route du serveur de dev scanne
 * le disque (profondeur 3, jusqu'a 40 depots, budget de 8 secondes) : acceptable
 * pour un panneau qu'on ouvre a la demande, jamais pour une bande permanente
 * montee a chaque chargement de page. La branche et le sha viennent du tampon,
 * qui les a lus UNE fois, au demarrage du serveur ou au build.
 */

/**
 * Les quatre etats de la lecture, distincts a dessein.
 *
 * `missing` n'est PAS une erreur : un deploiement fabrique avant l'arrivee du
 * tampon n'en a pas, et l'app doit demarrer normalement. `error` couvre le seul
 * cas anormal : un tampon present mais illisible.
 */
export type BuildStampState =
  | { kind: 'loading' }
  | { kind: 'found'; stamp: TakiBuildStamp }
  | { kind: 'missing'; reason: string }
  | { kind: 'error'; message: string };

/** Cache de module : une seule lecture par chargement de page. */
let pending: Promise<BuildStampState> | null = null;

function loadOnce(): Promise<BuildStampState> {
  if (pending != null) return pending;
  pending = loadTakiBuildStamp().then(
    (outcome): BuildStampState =>
      outcome.found
        ? { kind: 'found', stamp: outcome.stamp }
        : { kind: 'missing', reason: outcome.reason },
    (cause: unknown): BuildStampState => ({
      kind: 'error',
      message: cause instanceof Error ? cause.message : 'Tampon de build illisible.',
    }),
  );
  return pending;
}

/**
 * Lit le tampon une fois et rend l'etat courant. Le cache de module fait qu'un
 * seul fetch a lieu, meme quand deux bandes le consomment en meme temps.
 *
 * Pas de bouton "Recharger", pas de nouvelle tentative automatique : le tampon
 * est fige a la fabrication, le relire ne donnerait jamais autre chose.
 */
export function useBuildStamp(): BuildStampState {
  const [state, setState] = useState<BuildStampState>({ kind: 'loading' });

  useEffect(() => {
    let alive = true;
    void loadOnce().then((next) => {
      if (alive) setState(next);
    });
    return () => {
      alive = false;
    };
  }, []);

  return state;
}

/* -------------------------------------------------------------------------- */
/* Mise en forme de la version (pur)                                           */
/* -------------------------------------------------------------------------- */

/** Texte affiche quand une information n'a pas pu etre lue. Jamais un 0.0.0. */
export const UNKNOWN_VERSION = 'version inconnue';

/**
 * Version de plateforme AFFICHEE : du CalVer, majeur fige a 26, stocke en semver
 * valide (`26.32.0`) et affiche `26.32`. Le troisieme chiffre est reserve aux
 * correctifs poses sur une livraison deja deployee : a zero, il n'apporte rien a
 * l'oeil, donc il ne s'affiche pas ; non nul, il compte et il s'affiche.
 *
 * Une valeur qui n'est pas un semver a trois nombres est rendue TELLE QUELLE :
 * mieux vaut afficher une chaine inattendue que la tronquer en silence.
 *
 * ELLE VIT ICI POUR LA MEME RAISON QUE LE HOOK CI-DESSUS. Elle a ete ecrite pour
 * la ligne de version du menu DEV (`dev/versionLine.ts`), mais l'ecran "Mon
 * compte" l'affiche desormais en PRODUCTION : retirer la greffe DEV ne doit pas
 * emporter la mise en forme de la version avec elle.
 */
export function formatPlatformVersion(version: string | null): string {
  if (version == null || version.length === 0) return UNKNOWN_VERSION;
  const parts = /^(\d+)\.(\d+)\.(\d+)$/.exec(version);
  if (parts == null) return version;
  const [, major, minor, patch] = parts;
  if (major == null || minor == null || patch == null) return version;
  return patch === '0' ? `${major}.${minor}` : version;
}
